/**
 * None/Direct Payment Provider
 * Handles direct order creation without payment processing
 * Used for quote requests, invoice orders, or manual payment processing
 */

import { PaymentProvider } from './base.js';
import { getCurrencyCode } from '../../core/i18n.js';
import { clearStorage } from '../../core/storage.js';

export class NoneProvider extends PaymentProvider {
    constructor(config, system) {
        super(config, system);
        this.providerType = 'none';
    }

    async processOrder(order) {
        // Direct orders should use the customer form approach
        console.warn('NoneProvider.processOrder is deprecated. Use customer form approach with processDirectOrder instead.');
        
        // Redirect to checkout page with customer form
        const checkoutUrl = this.config.checkoutUrl || '/checkout/';
        window.location.href = checkoutUrl;
        
        return {
            success: true,
            message: 'Redirecting to customer form checkout',
            checkoutUrl: checkoutUrl
        };
    }

    async confirmOrder(orderId) {
        // Direct orders are already confirmed - use standard confirmation
        const url = `${this.config.confirmApiUrl}?merchantId=${this.config.merchantId}&orderId=${orderId}&provider=none`;
        
        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Accept-Language': this.config.locale,
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            }
        });

        if (!response.ok) {
            throw new Error(`None provider confirmation error! status: ${response.status}`);
        }

        return await response.json();
    }

    renderCheckout(data) {
        // None provider uses customer form checkout
        console.log('NoneProvider: Using customer form checkout approach');
        
        // If we somehow end up here, show a message directing to the form
        const checkoutContainer = document.getElementById('none-checkout') || 
                                document.getElementById('checkout-container');
                                
        if (checkoutContainer) {
            checkoutContainer.innerHTML = `
                <div class="none-redirect-info" style="padding: 20px; background: #e8f5e9; border: 1px solid #4caf50; border-radius: 4px; margin: 20px 0;">
                    <h3 style="color: #2e7d32; margin-top: 0;">📋 Direktbeställning</h3>
                    <p style="color: #2e7d32; margin: 10px 0;">
                        Fyll i dina uppgifter i formuläret för att slutföra beställningen.
                        Ingen betalning kommer att behandlas - beställningen sparas direkt.
                    </p>
                    <p style="color: #2e7d32; margin: 10px 0; font-size: 14px;">
                        Detta kan användas för offertförfrågningar eller fakturering.
                    </p>
                </div>
            `;
        }
    }

    /**
     * Process direct order with customer form data
     * This is the main method used by customer-form.js
     * @param {Object} orderData - Order data from customer form
     * @returns {Promise<Object>} API response
     */
    static async processDirectOrder(orderData) {
        const response = await fetch('/cart/api/v1/order/direct', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(orderData)
        });

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Direct order API error! status: ${response.status} - ${errorText}`);
        }

        return await response.json();
    }

    /**
     * Initiate a None payment via /payments/initiate/none (Phase 3).
     * Used ONLY when a gift card payment is applied - /order/direct has no
     * gift card support. A fully covered order completes synchronously and
     * the response carries status: "completed".
     * @param {Object} orderData - InitiatePaymentRequest-shaped order data
     * @returns {Promise<Object>} API response
     */
    static async initiatePayment(orderData) {
        const response = await fetch('/cart/api/v1/payments/initiate/none', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(orderData)
        });

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`None API error! status: ${response.status} - ${errorText}`);
        }

        return await response.json();
    }

    /**
     * Build an InitiatePaymentRequest for /payments/initiate/none (Phase 3).
     * Reuses the direct-order builder for customer/rows/discounts/shipping
     * and adds the initiate-specific fields plus giftCardPayments (B6: card
     * number + PIN only - the server computes the amount).
     * @param {Object} customerFormData - Customer form data
     * @param {Array} cartItems - Cart items
     * @param {Object} config - Configuration
     * @returns {Object} Order data for the initiate API
     */
    static buildInitiateOrderData(customerFormData, cartItems, config) {
        const base = this.buildOrderData(customerFormData, cartItems, config);

        // Get gift card payments from the GiftCardPaymentManager (max 1 card, B6)
        const giftCardPayments =
            window.ecommerce?.checkout?.giftCardPaymentManager?.getGiftCardPayments?.() || [];

        return {
            merchantId: base.merchantId,
            currency: base.purchaseCurrency,
            orderReference: `${Date.now()}`,
            returnUrl: this.absoluteUrl(config.confirmUrl, '/thank-you/'),
            cancelUrl: this.absoluteUrl(config.checkoutUrl, '/checkout/'),
            locale: base.locale,
            purchaseCountry: base.purchaseCountry,
            customer: base.customer,
            orderRows: base.orderRows,
            deliveryAddress: base.deliveryAddress,
            sendReceiptToDeliveryAddress: base.sendReceiptToDeliveryAddress,
            termsUrl: config.termsUrl ? this.absoluteUrl(config.termsUrl) : null,
            discounts: base.discounts,
            // Use capital S to match InitiatePaymentRequest.ShippingData property
            ShippingData: base.ShippingData,
            giftCardPayments: giftCardPayments
        };
    }

    /**
     * Build order data for direct order API
     * Similar to BamboraProvider.buildOrderData but for /api/v1/order/direct endpoint
     * @param {Object} customerFormData - Customer form data
     * @param {Array} cartItems - Cart items
     * @param {Object} config - Configuration
     * @returns {Object} Order data for API
     */
    static buildOrderData(customerFormData, cartItems, config) {
        // NOTE: Total amount is NOT calculated in JavaScript
        // Backend calculates the correct total from OrderRows, handling B2C/B2B VAT correctly
        
        // Build customer object according to CustomerModel structure
        const customer = {
            name: customerFormData.name || '',
            lastname: customerFormData.lastname || '',
            companyName: customerFormData.companyName || null,
            address: customerFormData.address || '',
            zip: customerFormData.zip || '',
            city: customerFormData.city || '',
            country: customerFormData.country || 'SE',
            email: customerFormData.email || '',
            phone: customerFormData.phone || '',
            type: customerFormData.companyName ? 1 : 0, // 1 = Company, 0 = Private
            organizationRegistrationId: customerFormData.organizationRegistrationId || null,
            vatId: null
        };

        // Build order rows according to OrderRowModel structure
        const orderRows = cartItems.map(item => {
            // Identity (GUIDs are translation-proof), quantity, and for gift cards the chosen
            // amount via giftCardData. Type and TaxRate are intentionally NOT sent - the server
            // derives them from the database.
            const orderRow = {
                productNr: item.ProductNr,
                productName: item.ProductName,
                productPrice: parseFloat(item.ProductPrice),
                orderCount: item.OrderCount,
                orderUnit: 'st',
                rowSum: parseFloat(item.ProductPrice) * item.OrderCount,
                productUrl: item.ProductUrl || '',
                imageUrl: item.ImageUrl || ''
            };

            // Forward the translation-proof identifiers when present
            if (item.ProductId) {
                orderRow.productId = item.ProductId;
            }
            if (item.ProductVariantId) {
                orderRow.productVariantId = item.ProductVariantId;
            }
            
            // CRITICAL: Add GiftCardData if this is a gift card
            // This ensures gift card information is preserved through the order flow
            if (item.isGiftCard && item.giftCardData) {
                orderRow.giftCardData = item.giftCardData;
                console.log('Added GiftCardData to order row for product:', item.ProductNr, 'Data:', item.giftCardData);
            }
            
            return orderRow;
        });

        // Build delivery address if different delivery address is selected
        let deliveryAddress = null;
        if (customerFormData.differentDeliveryAddress) {
            deliveryAddress = {
                name: customerFormData.deliveryName || '',
                lastname: customerFormData.deliveryLastname || '',
                companyName: customerFormData.deliveryCompany || null,
                address: customerFormData.deliveryAddress || '',
                zip: customerFormData.deliveryZip || '',
                city: customerFormData.deliveryCity || '',
                country: 'SE',
                email: customerFormData.deliveryEmail || customer.email,
                phone: customerFormData.deliveryPhone || customer.phone
            };
        }

        // Get discount data from DiscountManager if available
        let discounts = [];
        
        // Try to get discount data from global ecommerce system
        if (window.ecommerce && window.ecommerce.checkout && window.ecommerce.checkout.discountManager) {
            const discountManager = window.ecommerce.checkout.discountManager;
            if (discountManager.discountStack && discountManager.discountStack.length > 0) {
                discounts = discountManager.discountStack.map(discount => ({
                    code: discount.code,
                    amount: Math.round(discount.discountAmount * 100), // Convert to öre
                    description: discount.description || discount.name || `Rabatt: ${discount.code}`
                }));
                console.log('NoneProvider: Including multiple discounts in order:', discounts);
            }
        }

        // Get currency from i18n system or config
        const currency = getCurrencyCode() || config.purchaseCurrency || 'SEK';
        const locale = config.locale || 'sv-SE';
        
        // Create order data according to OrderWithCustomerModel structure
        return {
            merchantId: config.merchantId || '00000000-0000-0000-0000-000000000001',
            purchaseCountry: config.purchaseCountry || 'se',
            purchaseCurrency: currency,
            locale: locale,
            customer: customer,
            orderRows: orderRows,
            deliveryAddress: deliveryAddress,
            sendReceiptToDeliveryAddress: customerFormData.sendReceiptToDeliveryAddress === 'on',
            // Include discount data if available
            discounts: discounts,
            // CRITICAL: Include ShippingData so OrderController can create shipping OrderRow
            // Use capital S to match OrderWithCustomerModel.ShippingData property
            ShippingData: customerFormData.shippingData
        };
    }
}
